import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Server, Database, FileText, Download, Trash2, RefreshCw, ChevronLeft, ChevronRight, Calendar, Activity, HardDrive, Loader2 } from 'lucide-react';

export default function SystemPage() {
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPages, setLogsPages] = useState(1);
  const [logAction, setLogAction] = useState('');
  const [logEntity, setLogEntity] = useState('');
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [logsSummary, setLogsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      const params = { page: logsPage, limit: 30 };
      if (logAction) params.action = logAction;
      if (logEntity) params.entity_type = logEntity;
      const res = await api.get('/system/logs', { params });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
      setLogsPages(res.data.pages || 1);
    } catch (e) { console.error(e); }
  }, [logsPage, logAction, logEntity]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [backupsRes, statsRes, summaryRes] = await Promise.all([
        api.get('/system/backups'),
        api.get('/system/stats'),
        api.get('/system/logs/summary'),
      ]);
      setBackups(backupsRes.data.backups || []);
      setStats(statsRes.data);
      setLogsSummary(summaryRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const res = await api.post('/system/backup');
      toast.success(`Backup ${res.data.status}: ${res.data.backup_id}`);
      fetchAll();
    } catch (e) { toast.error('Backup failed'); }
    finally { setBackingUp(false); }
  };

  const handleDeleteBackup = async (id) => {
    if (!window.confirm('Delete this backup?')) return;
    try { await api.delete(`/system/backups/${id}`); toast.success('Deleted'); fetchAll(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="system-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Server className="h-6 w-6 text-primary" /> System Management</h1>
        <p className="text-sm text-muted-foreground mt-1">Activity logs, backups, and system statistics</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card><CardContent className="p-4 flex items-center gap-3"><Database className="h-5 w-5 text-primary" /><div><p className="text-xs text-muted-foreground">Total Documents</p><p className="text-xl font-bold">{stats.total_documents}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><Activity className="h-5 w-5 text-blue-500" /><div><p className="text-xs text-muted-foreground">Today's Actions</p><p className="text-xl font-bold">{logsSummary?.today_logs || 0}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><HardDrive className="h-5 w-5 text-green-500" /><div><p className="text-xs text-muted-foreground">Backups</p><p className="text-xl font-bold">{backups.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><FileText className="h-5 w-5 text-orange-500" /><div><p className="text-xs text-muted-foreground">Total Logs</p><p className="text-xl font-bold">{logsSummary?.total_logs || 0}</p></div></CardContent></Card>
        </div>
      )}

      <Tabs defaultValue="logs">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="logs" data-testid="tab-logs"><FileText className="h-3.5 w-3.5 mr-1.5" /> Logs</TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups"><HardDrive className="h-3.5 w-3.5 mr-1.5" /> Backups</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats"><Database className="h-3.5 w-3.5 mr-1.5" /> DB Stats</TabsTrigger>
        </TabsList>

        {/* Activity Logs Tab */}
        <TabsContent value="logs" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-3">
            <Input placeholder="Filter by action..." value={logAction} onChange={(e) => { setLogAction(e.target.value); setLogsPage(1); }} className="w-48" data-testid="log-action-filter" />
            <Select value={logEntity || 'all'} onValueChange={(v) => { setLogEntity(v === 'all' ? '' : v); setLogsPage(1); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Entity Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="sale">Sales</SelectItem>
                <SelectItem value="product">Products</SelectItem>
                <SelectItem value="user">Users</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.log_id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{log.user_name}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{log.action}</Badge></TableCell>
                      <TableCell className="text-xs">{log.entity_type}{log.entity_id ? ` / ${log.entity_id.slice(-8)}` : ''}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{log.details}</TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No activity logs found.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {logsPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={logsPage <= 1} onClick={() => setLogsPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-muted-foreground">Page {logsPage} of {logsPages}</span>
              <Button variant="outline" size="sm" disabled={logsPage >= logsPages} onClick={() => setLogsPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </TabsContent>

        {/* Backups Tab */}
        <TabsContent value="backups" className="mt-4 space-y-4">
          <Button onClick={handleBackup} disabled={backingUp} data-testid="create-backup-btn">
            {backingUp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Create Backup
          </Button>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Backup ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map(b => (
                    <TableRow key={b.backup_id}>
                      <TableCell className="text-xs font-mono">{b.backup_id}</TableCell>
                      <TableCell><Badge variant={b.status === 'completed' ? 'default' : 'destructive'}>{b.status}</Badge></TableCell>
                      <TableCell className="text-sm">{b.size_mb ? `${b.size_mb} MB` : '—'}</TableCell>
                      <TableCell className="text-sm">{b.created_by_name || b.created_by}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-right"><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteBackup(b.backup_id)}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}
                  {backups.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No backups yet. Create one to get started.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DB Stats Tab */}
        <TabsContent value="stats" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Database Collections</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Collection</TableHead><TableHead className="text-right">Documents</TableHead></TableRow></TableHeader>
                <TableBody>
                  {stats && Object.entries(stats.collections).map(([name, count]) => (
                    <TableRow key={name}>
                      <TableCell className="font-medium capitalize">{name.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="text-right font-mono">{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
